import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]{4,}$/
const GA4_ID_PATTERN = /^G-[A-Z0-9]{4,}$/

export default function WebsiteDetails() {
  const user = useUser()
  const { websiteId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [gtms, setGtms] = useState([])
  const [ga4, setGa4] = useState([])
  const [legacy, setLegacy] = useState([])
  const [url, setUrl] = useState("")
  const [showGtm, setShowGtm] = useState(true)
  const [showGa4, setShowGa4] = useState(true)
  const [showLegacy, setShowLegacy] = useState(true)
  const [gtmOwners, setGtmOwners] = useState({})
  const [savingOwners, setSavingOwners] = useState(false)

  const fetchDetails = useCallback(async () => {
    if (!user || !websiteId) return

    const gtmPromise = supabase
      .from("gtm_instances")
      .select("*")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })

    const ga4Promise = supabase
      .from("ga4_instances")
      .select("*")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })

    const legacyPromise = supabase
      .from("legacy_code_instances")
      .select("id, code, url, found_date, removed_date")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })

    const [{ data: gtmData }, { data: ga4Data }, { data: legacyData }] =
      await Promise.all([gtmPromise, ga4Promise, legacyPromise])

    if (gtmData) {
      setGtms(gtmData)
      const owners = {}
      gtmData.forEach((row) => {
        if (row.gtm_id) owners[row.gtm_id] = row.event_owner || ""
      })
      setGtmOwners(owners)
    }
    if (ga4Data) setGa4(ga4Data)
    if (legacyData) setLegacy(legacyData)

    const firstUrl =
      gtmData?.[0]?.url || ga4Data?.[0]?.url || legacyData?.[0]?.url || ""
    setUrl((currentUrl) => currentUrl || firstUrl)
  }, [user, websiteId])

  useEffect(() => {
    fetchDetails()
  }, [fetchDetails])

  useEffect(() => {
    if (!user || !websiteId) return

    const loadWebsiteDetails = async () => {
      const { data, error } = await supabase
        .from("website_urls")
        .select("url")
        .eq("id", websiteId)
        .single()

      if (!error && data) {
        if (data.url) setUrl(data.url)
      }
    }

    loadWebsiteDetails()
  }, [user, websiteId])

  const handleAddGtm = async () => {
    if (!user || !websiteId) return
    const input = window.prompt(
      "Enter the GTM Container ID (for example, GTM-XXXXXXX):"
    )
    const trimmed = input?.trim()
    if (!trimmed) return

    const normalized = trimmed.toUpperCase()
    if (!GTM_ID_PATTERN.test(normalized)) {
      window.alert("Invalid GTM container ID format.")
      return
    }

    if (gtms.some((row) => row.gtm_id?.toUpperCase() === normalized)) {
      window.alert("This GTM code already exists for the selected website.")
      return
    }

    const payload = {
      user_id: user.id,
      website_id: websiteId,
      gtm_id: normalized,
      url,
      found_method: "Manually Added",
      found_date: new Date().toISOString(),
    }

    const { error } = await supabase.from("gtm_instances").insert(payload)
    if (error) {
      console.error("Failed to add GTM code:", error)
      window.alert("Failed to add GTM code. Please try again.")
      return
    }

    fetchDetails()
  }

  const handleOwnerChange = (gtmId, value) => {
    setGtmOwners((prev) => ({ ...prev, [gtmId]: value }))
  }

  const saveEventOwners = async () => {
    if (!user || !websiteId) return
    const updates = gtms
      .map((row) => ({
        row,
        nextOwner: (gtmOwners[row.gtm_id] || "").trim(),
        currentOwner: (row.event_owner || "").trim(),
      }))
      .filter(({ nextOwner, currentOwner }) => nextOwner !== currentOwner)

    if (updates.length === 0) {
      window.alert("No Event Owner changes to save.")
      return
    }

    setSavingOwners(true)
    try {
      await Promise.all(
        updates.map(({ row, nextOwner }) =>
          supabase
            .from("gtm_instances")
            .update({ event_owner: nextOwner || null })
            .match({
              user_id: user.id,
              website_id: websiteId,
              gtm_id: row.gtm_id,
            })
        )
      )
      window.alert("Saved Event Owner updates.")
      fetchDetails()
    } catch (error) {
      console.error("Failed to save Event Owners", error)
      window.alert("Failed to save Event Owner changes. Please try again.")
    } finally {
      setSavingOwners(false)
    }
  }

  const handleAddGa4 = async () => {
    if (!user || !websiteId) return
    const input = window.prompt(
      "Enter the GA4 Measurement ID (for example, G-XXXXXXX):"
    )
    const trimmed = input?.trim()
    if (!trimmed) return

    const normalized = trimmed.toUpperCase()
    if (!GA4_ID_PATTERN.test(normalized)) {
      window.alert("Invalid GA4 measurement ID format.")
      return
    }

    if (ga4.some((row) => row.ga_id?.toUpperCase() === normalized)) {
      window.alert("This GA4 ID already exists for the selected website.")
      return
    }

    const payload = {
      user_id: user.id,
      website_id: websiteId,
      ga_id: normalized,
      url,
      found_date: new Date().toISOString(),
    }

    const { error } = await supabase.from("ga4_instances").insert(payload)
    if (error) {
      console.error("Failed to add GA4 ID:", error)
      window.alert("Failed to add GA4 ID. Please try again.")
      return
    }

    fetchDetails()
  }

  return (
    <div className="page-container">
      <h2>Website Details for {url}</h2>
      <button
        onClick={() => {
          const fromPath = location.state?.from
          if (fromPath) navigate(fromPath)
          else navigate("/website-audit/scraping")
        }}
      >
        Back
      </button>

      <div style={{ marginTop: "1rem" }}>
        <button onClick={() => setShowGtm((p) => !p)}>
          {showGtm ? "Hide" : "Expand"} GTMs
        </button>
        {showGtm && (
          <>
            <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.5rem" }}>
              <button onClick={handleAddGtm}>Add GTM Code</button>
              <button onClick={saveEventOwners} disabled={savingOwners}>
                {savingOwners ? "Saving..." : "Save Event Owners"}
              </button>
            </div>
            <table className="ui-table"
              border="1"
              cellPadding="8"
              style={{ width: "100%", marginTop: "0.5rem" }}
            >
              <thead>
                <tr>
                  <th>GTM ID</th>
                  <th>Found Date</th>
                  <th>Removed Date</th>
                  <th>Found Method</th>
                  <th>Owner</th>
                  <th>Explore</th>
                </tr>
              </thead>
              <tbody>
                {gtms.map((row) => (
                  <tr key={row.id} style={{ opacity: row.removed_date ? 0.5 : 1 }}>
                    <td>{row.gtm_id}</td>
                    <td>{row.found_date?.split("T")[0]}</td>
                    <td>{row.removed_date?.split("T")[0] || ""}</td>
                    <td>{row.found_method}</td>
                    <td>
                      <input
                        type="text"
                        value={gtmOwners[row.gtm_id] ?? ""}
                        onChange={(e) => handleOwnerChange(row.gtm_id, e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <button
                        onClick={() =>
                          navigate(`/website-audit/gtm-details/${websiteId}/${row.gtm_id}`)
                        }
                      >
                        Explore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div style={{ marginTop: "1rem" }}>
        <button onClick={() => setShowGa4((p) => !p)}>
          {showGa4 ? "Hide" : "Expand"} GA4 IDs
        </button>
        {showGa4 && (
          <>
            <div style={{ marginTop: "0.5rem" }}>
              <button onClick={handleAddGa4}>Add GA4 ID</button>
            </div>
            <table className="ui-table"
              border="1"
              cellPadding="8"
              style={{ width: "100%", marginTop: "0.5rem" }}
            >
              <thead>
                <tr>
                  <th>GA4 ID</th>
                  <th>Found Date</th>
                  <th>Removed Date</th>
                  <th>Owner</th>
                  <th>Explore GA4</th>
                </tr>
              </thead>
              <tbody>
                {ga4.map((row) => (
                  <tr key={row.id} style={{ opacity: row.removed_date ? 0.5 : 1 }}>
                    <td>{row.ga_id}</td>
                    <td>{row.found_date?.split("T")[0]}</td>
                    <td>{row.removed_date?.split("T")[0] || ""}</td>
                    <td>{row.event_owner || "—"}</td>
                    <td>
                      <button
                        onClick={() =>
                          navigate(`/website-audit/analytics-details/${websiteId}/${row.ga_id}`, {
                            state: {
                              from: `${location.pathname}${location.search}`,
                            },
                          })
                        }
                      >
                        Explore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div style={{ marginTop: "1rem" }}>
        <button onClick={() => setShowLegacy((p) => !p)}>
          {showLegacy ? "Hide" : "Expand"} Legacy Codes
        </button>
        {showLegacy && (
          <table className="ui-table"
            border="1"
            cellPadding="8"
            style={{ width: "100%", marginTop: "0.5rem" }}
          >
            <thead>
              <tr>
                <th>Code</th>
                <th>Found Date</th>
                <th>Removed Date</th>
              </tr>
            </thead>
            <tbody>
              {legacy.map((row) => (
                <tr key={row.id} style={{ opacity: row.removed_date ? 0.5 : 1 }}>
                  <td>{row.code}</td>
                  <td>{row.found_date?.split("T")[0]}</td>
                  <td>{row.removed_date?.split("T")[0] || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
